import {
  Button,
  Callout,
  Classes,
  H3,
  H5,
  Icon,
  Intent,
  Spinner,
} from '@blueprintjs/core';
import * as React from 'react';
import { usePortalConnections } from 'vet-bones/utils/portal';

import { TEST_IDS } from 'src/constants/testIds';
import { ConnectionMenu } from 'src/containers/ConnectionMenu/ConnectionMenu';
import { connectionSelector } from 'src/selectors/connection';
import { isConnectedSelector } from 'src/selectors/isConnected';
import { isConnectingSelector } from 'src/selectors/isConnecting';
import { connectToStardog } from 'src/store/connections/connectToStardog';
import * as copy from 'src/templates/copy';
import { icons } from 'src/templates/icons';
import { colors } from 'src/templates/styles/colors';
import { useReduxDispatch } from 'src/utils/hooks/useReduxDispatch';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useSetupDefaultPortalConnection } from 'src/utils/hooks/useSetupDefaultPortalConnection';

type NewVirtualGraphTileProps = {
  handleProceedWithConnection: () => void;
};

export const NewVirtualGraphTile: React.VFC<NewVirtualGraphTileProps> = ({
  handleProceedWithConnection,
}) => {
  const dispatch = useReduxDispatch();
  const connection = useReduxSelector(connectionSelector);
  const isConnected = useReduxSelector(isConnectedSelector);
  const isConnecting = useReduxSelector(isConnectingSelector);
  const alreadyProceeded = React.useRef(false);

  const connections = usePortalConnections();
  useSetupDefaultPortalConnection({ connect: false });

  const proceed = React.useCallback(() => {
    // In case someone clicks the back button, don't autoProceed again
    if (!alreadyProceeded.current) {
      alreadyProceeded.current = true;
      handleProceedWithConnection();
    }
  }, [handleProceedWithConnection]);

  const [shouldProceed, setShouldProceed] = React.useState(false);
  React.useEffect(() => {
    if (shouldProceed && isConnected) {
      proceed();
    }
  }, [isConnected, proceed, shouldProceed]);

  return (
    <Callout className="sd-resource-type-panel__tile" intent={Intent.NONE}>
      <H3>
        {
          copy.components.projectResourceDialog.resourceTypePanel.connection
            .title
        }
      </H3>
      <Icon
        color={isConnected ? colors.darkIcon : undefined}
        icon={icons.connection}
        iconSize={24}
      />
      {!connection || isConnecting ? (
        <Spinner />
      ) : (
        <>
          {connections.data && connections.data.length > 1 ? (
            <H5>{connection.name}</H5>
          ) : null}
          <span>{connection.endpoint}</span>
          <span>
            {copy.components.projectResourceDialog.resourceTypePanel.connection.username(
              connection.username
            )}
          </span>
          <Button
            data-testid={
              TEST_IDS.projectResourceDialog.resourceTypePanel.proceedButton
            }
            intent={Intent.PRIMARY}
            large
            onClick={() => {
              setShouldProceed(true);
              dispatch(
                connectToStardog({ connection, shouldNotNotifySuccess: true })
              );
            }}
            text={
              copy.components.projectResourceDialog.resourceTypePanel.connection
                .proceedButton
            }
          />
          {connections.data && connections.data.length > 1 ? (
            <div className={Classes.TEXT_SMALL}>
              <span>
                {
                  copy.components.projectResourceDialog.resourceTypePanel
                    .connection.otherConnection
                }
              </span>
              <ConnectionMenu connect={false} disabled={isConnecting}>
                <a
                  data-testid={
                    TEST_IDS.projectResourceDialog.resourceTypePanel
                      .switchConnection
                  }
                  role="link"
                  tabIndex={0}
                >
                  {
                    copy.components.projectResourceDialog.resourceTypePanel
                      .connection.selectHere
                  }
                </a>
              </ConnectionMenu>
            </div>
          ) : null}
        </>
      )}
    </Callout>
  );
};
